import { api, type Item, type Location } from "../api";
import { promptGroceryLinksForItem } from "../components/groceryLinkPrompt";
import { showItemSheet } from "../components/itemForm";
import { applyManifestRowQuantity, renderManifestRow } from "../components/manifestRow";
import { setTallyQuantity } from "../components/tallyControl";
import { formatManifestMetaParts } from "../utils/manifestMeta";
import { formatItemPickerLabel } from "../utils/itemPickerLabel";
import { showPicker, showSheet } from "../components/modal";
import { openScannerOverlay } from "../components/scannerOverlay";
import { createSwipeRow } from "../components/swipeRow";
import type { ScanAddDraft } from "../scanDraft";
import { showUndoToast, finalizePendingToast } from "../components/toast";
import { msg } from "../messages";
import { isNavigationStale } from "../navigation";
import {
    DEFAULT_ITEM_SORT,
    ITEM_SORT_LABELS,
    sortItems,
    type ItemSortOption,
} from "../utils/itemSort";
import { debounce } from "../utils/dates";
import { createIcon } from "../utils/icons";

interface PantryState {
    locations: Location[];
    items: Item[];
    categories: string[];
    locationFilter: number | null;
    categoryFilter: string;
    expiringFilter: string;
    stockedFilter: string;
    searchQuery: string;
    sortBy: ItemSortOption;
}

let state: PantryState = {
    locations: [],
    items: [],
    categories: [],
    locationFilter: null,
    categoryFilter: "",
    expiringFilter: "",
    stockedFilter: "",
    searchQuery: "",
    sortBy: DEFAULT_ITEM_SORT,
};

let manifestUpdateInProgress = false;
let manifestUpdatePending = false;
let manifestUpdateArgs: { listEl: HTMLElement; container: HTMLElement } | null = null;
const pendingItemRemovals = new Set<number>();
let pendingFocusItemId: number | null = null;

function applyPantryFiltersFromHash(): void {
    const query = location.hash.split("?")[1];
    if (!query) {
        pendingFocusItemId = null;
        return;
    }

    const params = new URLSearchParams(query);

    if (params.get("reset") === "1") {
        state.locationFilter = null;
        state.categoryFilter = "";
        state.expiringFilter = "";
        state.stockedFilter = "";
        state.searchQuery = "";
        state.sortBy = DEFAULT_ITEM_SORT;
    }

    const expiring = params.get("expiring");
    if (expiring) {
        state.expiringFilter = expiring;
    }

    const expired = params.get("expired");
    if (expired === "1") {
        state.expiringFilter = "expired";
    }

    const stocked = params.get("stocked");
    if (stocked === "1" || stocked === "0") {
        state.stockedFilter = stocked;
    }

    const item = params.get("item");
    pendingFocusItemId = item && /^\d+$/.test(item) ? Number(item) : null;
}

function hasActiveFilters(): boolean {
    return state.locationFilter !== null || state.stockedFilter !== "" || state.expiringFilter !== "";
}

const MANIFEST_STAGGER_BASE = 4;
const MANIFEST_STAGGER_CAP = 14;
const QUANTITY_REORDER_DEBOUNCE_MS = 1200;
const PANTRY_MOTION_MS = 400;
const PANTRY_FLIP_MS = 400;
const PANTRY_EMPTY_MS = 400;
const PANTRY_REORDER_MS = 480;
const PANTRY_SEARCH_REORDER_MS = 220;
const PANTRY_EASE = "cubic-bezier(0.25, 1, 0.5, 1)";
const PANTRY_FLIP_CLASS = "pantry-flip-motion";

interface PantryRemovalAnchor {
    beforeId: number | null;
}

type ListEntrance = "initial" | "refresh";

let manifestGeneration = 0;
let lastQuantityChangeId: number | null = null;

function isQuantitySort(): boolean {
    return state.sortBy === "quantity-asc" || state.sortBy === "quantity-desc";
}

function getSwipeRowForItem(listEl: HTMLElement, itemId: number): HTMLElement | null {
    return (
        (listEl
            .querySelector(`.manifest-row[data-item-id="${itemId}"]`)
            ?.closest(".swipe-row") as HTMLElement | null) ?? null
    );
}

function focusPantryItem(listEl: HTMLElement, itemId: number | null): void {
    if (!itemId) return;
    const row = getSwipeRowForItem(listEl, itemId);
    if (!row) return;
    const rowContent = row.querySelector<HTMLElement>(".swipe-row-content");
    rowContent?.classList.add("swipe-row-content--focused");
    requestAnimationFrame(() => {
        row.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    window.setTimeout(() => {
        rowContent?.classList.remove("swipe-row-content--focused");
    }, 1800);
}

function getPantryRemovalAnchor(rowEl: HTMLElement): PantryRemovalAnchor {
    const next = rowEl.nextElementSibling as HTMLElement | null;
    return {
        beforeId: next?.dataset.itemId ? Number(next.dataset.itemId) : null,
    };
}

function resetPantryRowMotion(row: HTMLElement): void {
    row.getAnimations().forEach((anim) => anim.cancel());
    row.classList.remove(
        "cascade-glide",
        PANTRY_FLIP_CLASS,
        "swipe-row--reordering",
        "swipe-row--reorder-active",
        "swipe-row--reorder-partner",
    );
    row.style.removeProperty("transform");
    row.style.removeProperty("transition");
    row.style.removeProperty("will-change");
}

interface PantryFlipRun {
    movers: HTMLElement[];
    finished: Promise<HTMLElement[]>;
}

function startPantryFlip(
    siblings: HTMLElement[],
    topsBefore: Map<HTMLElement, number>,
    durationMs = PANTRY_FLIP_MS,
): PantryFlipRun {
    const movers: HTMLElement[] = [];
    const animations: Animation[] = [];

    for (const row of siblings) {
        resetPantryRowMotion(row);

        const delta = topsBefore.get(row)! - row.getBoundingClientRect().top;
        if (Math.abs(delta) < 0.5) continue;

        row.classList.add(PANTRY_FLIP_CLASS);
        movers.push(row);

        animations.push(
            row.animate([{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }], {
                duration: durationMs,
                easing: PANTRY_EASE,
                fill: "forwards",
            }),
        );
    }

    const finished =
        animations.length === 0
            ? Promise.resolve(movers)
            : Promise.all(animations.map((anim) => anim.finished.catch(() => undefined))).then(
                  () => movers,
              );

    return { movers, finished };
}

function clearPantryFlip(movers: HTMLElement[]): void {
    for (const row of movers) {
        resetPantryRowMotion(row);
    }
}

function preparePantryRowForExit(rowEl: HTMLElement): void {
    const content = rowEl.querySelector<HTMLElement>(".swipe-row-content");
    if (content) {
        content.style.transform = "";
        content.classList.remove("is-dragging");
    }
}

function runPantryExitAnimation(rowEl: HTMLElement): Animation {
    return rowEl.animate(
        [
            { opacity: 1, transform: "translateX(0)" },
            { opacity: 0, transform: "translateX(-12px)" },
        ],
        { duration: PANTRY_MOTION_MS, easing: PANTRY_EASE, fill: "forwards" },
    );
}

function snapshotPantryRowPin(
    listEl: HTMLElement,
    rowEl: HTMLElement,
): { top: number; height: number } {
    const listRect = listEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    return {
        top: rowRect.top - listRect.top + listEl.scrollTop,
        height: rowRect.height,
    };
}

function pinPantryRowOverlay(rowEl: HTMLElement, pin: { top: number; height: number }): void {
    rowEl.classList.add("pantry-row--exiting-overlay");
    rowEl.style.top = `${pin.top}px`;
    rowEl.style.left = "0";
    rowEl.style.right = "0";
    rowEl.style.width = "100%";
    rowEl.style.height = `${pin.height}px`;
    rowEl.style.margin = "0";
}

async function animatePantryRowExitWithFlip(
    listEl: HTMLElement,
    rowEl: HTMLElement,
): Promise<void> {
    const siblings = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")].filter(
        (row) => row !== rowEl,
    );
    const topsBefore = new Map(siblings.map((row) => [row, row.getBoundingClientRect().top]));
    const pin = snapshotPantryRowPin(listEl, rowEl);

    pinPantryRowOverlay(rowEl, pin);
    void listEl.offsetHeight;

    resetPantryRowMotion(rowEl);
    preparePantryRowForExit(rowEl);

    const exitAnim = runPantryExitAnimation(rowEl);
    const flip = startPantryFlip(siblings, topsBefore, PANTRY_MOTION_MS);

    await Promise.all([flip.finished, exitAnim.finished.catch(() => undefined)]);

    rowEl.remove();
    clearPantryFlip(flip.movers);
}

async function insertPantryRowAnimated(
    listEl: HTMLElement,
    rowEl: HTMLElement,
    beforeEl?: HTMLElement | null,
): Promise<void> {
    const siblings = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")];
    const topsBefore = new Map(siblings.map((row) => [row, row.getBoundingClientRect().top]));

    rowEl.style.opacity = "0";
    if (beforeEl) {
        listEl.insertBefore(rowEl, beforeEl);
    } else {
        listEl.appendChild(rowEl);
    }

    const flip = startPantryFlip(siblings, topsBefore, PANTRY_MOTION_MS);
    const rowEnter = rowEl.animate(
        [
            { opacity: 0, transform: "translateX(-12px)" },
            { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: PANTRY_MOTION_MS, easing: PANTRY_EASE },
    );

    await Promise.all([flip.finished, rowEnter.finished.catch(() => undefined)]);

    rowEl.style.removeProperty("opacity");
    clearPantryFlip(flip.movers);
}

function createPantryEmptyElement(container: HTMLElement): HTMLElement {
    const empty = document.createElement("div");
    empty.className = "empty-state pantry-empty";

    const message = document.createElement("p");
    message.textContent = msg("pantry/no-items");

    const actions = document.createElement("div");
    actions.className = "empty-actions";

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "btn btn-primary btn-block";
    addBtn.setAttribute("aria-label", "Add item");
    addBtn.append(createIcon("add", "icon-sm"), " Add item");
    addBtn.addEventListener("click", () => openAddSheet(container));

    const scanBtn = document.createElement("button");
    scanBtn.type = "button";
    scanBtn.className = "btn btn-secondary btn-block";
    scanBtn.setAttribute("aria-label", "Scan barcode");
    scanBtn.append(createIcon("photo_camera", "icon-sm"), " Scan barcode");
    scanBtn.addEventListener("click", () => {
        void openScannerOverlay().then((upc) => {
            if (upc) void handleBarcodeSearch(container, upc);
        });
    });

    actions.appendChild(addBtn);
    actions.appendChild(scanBtn);
    empty.appendChild(message);
    empty.appendChild(actions);
    return empty;
}

async function showPantryEmptyAnimated(listEl: HTMLElement, container: HTMLElement): Promise<void> {
    if (listEl.querySelector(".pantry-empty")) return;

    listEl.classList.remove("pantry-manifest--refresh");
    const empty = createPantryEmptyElement(container);
    empty.style.opacity = "0";
    listEl.appendChild(empty);

    await empty
        .animate(
            [
                { opacity: 0, transform: "translateY(10px)" },
                { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: PANTRY_EMPTY_MS, easing: PANTRY_EASE },
        )
        .finished.catch(() => undefined);

    empty.style.removeProperty("opacity");
}

async function removePantryEmptyAnimated(listEl: HTMLElement): Promise<void> {
    const empty = listEl.querySelector<HTMLElement>(".pantry-empty");
    if (!empty) return;

    await empty
        .animate(
            [
                { opacity: 1, transform: "translateY(0)" },
                { opacity: 0, transform: "translateY(8px)" },
            ],
            { duration: PANTRY_EMPTY_MS, easing: PANTRY_EASE },
        )
        .finished.catch(() => undefined);

    empty.remove();
}

async function removePantryRowAnimated(listEl: HTMLElement, itemId: number): Promise<boolean> {
    const rowEl = getSwipeRowForItem(listEl, itemId);
    if (!rowEl) return false;

    const willEmpty = listEl.querySelectorAll(".swipe-row").length === 1;
    await animatePantryRowExitWithFlip(listEl, rowEl);

    if (willEmpty) {
        const container = document.getElementById("app-main");
        if (container) await showPantryEmptyAnimated(listEl, container);
    }

    return true;
}

function buildSwipeRowForItem(item: Item, container: HTMLElement): HTMLElement {
    let manifestRow: HTMLElement;
    manifestRow = renderManifestRow(item, {
        onDecrement: () => {
            void changeQuantity(item.id, -1, manifestRow);
        },
        onIncrement: () => {
            void changeQuantity(item.id, 1, manifestRow);
        },
        onQuantitySet: (_rowItem, quantity) => {
            void setQuantityAbsolute(item.id, quantity, manifestRow);
        },
    });

    const swipeRow = createSwipeRow(manifestRow, {
        onEdit: () => {
            const current = state.items.find((i) => i.id === item.id);
            openEditSheet(container, current ?? item);
        },
    });
    swipeRow.dataset.itemId = String(item.id);
    return swipeRow;
}

function updatePantryRowFromItem(swipeRow: HTMLElement, item: Item, container: HTMLElement): void {
    const manifestRow = swipeRow.querySelector<HTMLElement>(".manifest-row");
    if (!manifestRow) return;

    manifestRow.dataset.itemId = String(item.id);
    swipeRow.dataset.itemId = String(item.id);

    const nameEl = manifestRow.querySelector<HTMLElement>(".manifest-name");
    if (nameEl && nameEl.textContent !== item.name) {
        nameEl.textContent = item.name;
    }

    const metaEl = manifestRow.querySelector<HTMLElement>(".manifest-meta");
    if (metaEl) {
        const parts = formatManifestMetaParts(item);
        const currentPrefix = metaEl.dataset.prefix ?? "";
        const currentExpiry = metaEl.dataset.expiry ?? "";
        if (currentPrefix !== parts.prefix || currentExpiry !== parts.expiryText) {
            metaEl.replaceChildren();
            if (parts.prefix) {
                metaEl.append(document.createTextNode(`${parts.prefix} • `));
            }
            const expirySpan = document.createElement("span");
            expirySpan.className = parts.expiryWarn
                ? "manifest-meta-expiry manifest-meta-expiry--warn"
                : "manifest-meta-expiry";
            expirySpan.textContent = parts.expiryText;
            metaEl.appendChild(expirySpan);
            metaEl.dataset.prefix = parts.prefix;
            metaEl.dataset.expiry = parts.expiryText;
        }
    }

    const tallyCount = manifestRow.querySelector<HTMLElement>(".tally-count");
    if (tallyCount) {
        const current = Number(tallyCount.dataset.value ?? "0");
        if (!Number.isNaN(current) && current !== item.quantity) {
            const direction = item.quantity > current ? "up" : "down";
            applyManifestRowQuantity(manifestRow, item.name, item.quantity, direction);
        }
    }
}

async function exitPantryRowFromList(listEl: HTMLElement, itemId: number): Promise<void> {
    const rowEl = getSwipeRowForItem(listEl, itemId);
    if (!rowEl) return;
    await animatePantryRowExitWithFlip(listEl, rowEl);
}

function pantryDomOrderMatchesSort(listEl: HTMLElement): boolean {
    const items = sortItems(state.items, state.sortBy, state.searchQuery);
    const targetIds = new Set(items.map((item) => item.id));
    const rows = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")].filter((row) =>
        targetIds.has(Number(row.dataset.itemId)),
    );
    if (rows.length !== items.length) return false;
    return rows.every((row, index) => Number(row.dataset.itemId) === items[index].id);
}

function syncPantryManifestOrder(
    listEl: HTMLElement,
    scrollToItemId: number | null = null,
    durationMs: number = PANTRY_REORDER_MS,
): void {
    if (pantryDomOrderMatchesSort(listEl)) return;

    const sorted = sortItems(state.items, state.sortBy, state.searchQuery);
    for (const item of sorted) {
        const row = getSwipeRowForItem(listEl, item.id);
        if (row) listEl.appendChild(row);
    }

    animateManifestReorder(listEl, scrollToItemId, durationMs);
}

async function syncPantryManifestFromState(
    listEl: HTMLElement,
    container: HTMLElement,
): Promise<void> {
    if (manifestUpdateInProgress) {
        manifestUpdatePending = true;
        manifestUpdateArgs = { listEl, container };
        return;
    }
    manifestUpdateInProgress = true;
    try {
        await syncPantryManifestFromStateInner(listEl, container);
    } finally {
        manifestUpdateInProgress = false;
        if (manifestUpdatePending && manifestUpdateArgs) {
            const args = manifestUpdateArgs;
            manifestUpdatePending = false;
            manifestUpdateArgs = null;
            void syncPantryManifestFromState(args.listEl, args.container);
        }
    }
}

async function syncPantryManifestFromStateInner(
    listEl: HTMLElement,
    container: HTMLElement,
): Promise<void> {
    const items = sortItems(state.items, state.sortBy, state.searchQuery);
    const targetIds = new Set(items.map((item) => item.id));
    const exitingRows = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")].filter(
        (row) => !targetIds.has(Number(row.dataset.itemId)),
    );

    if (items.length === 0) {
        const exitAnims: Animation[] = [];
        for (const row of exitingRows) {
            resetPantryRowMotion(row);
            preparePantryRowForExit(row);
            const pin = snapshotPantryRowPin(listEl, row);
            pinPantryRowOverlay(row, pin);
            exitAnims.push(runPantryExitAnimation(row));
        }
        await Promise.all(exitAnims.map((anim) => anim.finished.catch(() => undefined)));
        for (const row of exitingRows) {
            row.remove();
        }
        if (!listEl.querySelector(".pantry-empty")) {
            await showPantryEmptyAnimated(listEl, container);
        }
        return;
    }

    await removePantryEmptyAnimated(listEl);

    const itemById = new Map(items.map((item) => [item.id, item]));
    for (const row of [...listEl.querySelectorAll<HTMLElement>(".swipe-row")]) {
        const id = Number(row.dataset.itemId);
        const item = itemById.get(id);
        if (item) updatePantryRowFromItem(row, item, container);
    }

    const exitAnims: Animation[] = [];
    for (const row of exitingRows) {
        resetPantryRowMotion(row);
        preparePantryRowForExit(row);
        const pin = snapshotPantryRowPin(listEl, row);
        pinPantryRowOverlay(row, pin);
        exitAnims.push(runPantryExitAnimation(row));
    }

    const newRows: HTMLElement[] = [];
    for (const item of items) {
        if (getSwipeRowForItem(listEl, item.id)) continue;
        const rowEl = buildSwipeRowForItem(item, container);
        rowEl.style.opacity = "0";
        listEl.appendChild(rowEl);
        newRows.push(rowEl);
    }

    syncPantryManifestOrder(listEl, null);

    const enterAnims: Animation[] = [];
    for (const row of newRows) {
        enterAnims.push(
            row.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration: PANTRY_MOTION_MS,
                easing: PANTRY_EASE,
            }),
        );
    }

    await Promise.all([
        ...exitAnims.map((anim) => anim.finished.catch(() => undefined)),
        ...enterAnims.map((anim) => anim.finished.catch(() => undefined)),
    ]);

    for (const row of exitingRows) {
        row.remove();
    }
    for (const row of newRows) {
        row.style.removeProperty("opacity");
    }
}

function findPantryInsertBefore(listEl: HTMLElement, item: Item): HTMLElement | null {
    const sorted = sortItems(state.items, state.sortBy, state.searchQuery);
    const index = sorted.findIndex((i) => i.id === item.id);
    if (index < 0) return null;

    for (let i = index + 1; i < sorted.length; i++) {
        const row = getSwipeRowForItem(listEl, sorted[i].id);
        if (row) return row;
    }
    return null;
}

function getBottomScrollInset(): number {
    const styles = getComputedStyle(document.documentElement);
    const nav = parseFloat(styles.getPropertyValue("--bottom-nav-offset"));
    const pad = parseFloat(styles.getPropertyValue("--space-md"));
    return (Number.isFinite(nav) ? nav : 64) + (Number.isFinite(pad) ? pad : 16);
}

function scrollRowIntoViewClearingNav(row: HTMLElement): void {
    const inset = getBottomScrollInset();
    const rect = row.getBoundingClientRect();
    const viewBottom = window.innerHeight - inset;

    if (rect.bottom > viewBottom) {
        window.scrollBy({ top: rect.bottom - viewBottom, behavior: "smooth" });
        return;
    }
    if (rect.top < 0) {
        window.scrollBy({ top: rect.top, behavior: "smooth" });
    }
}

function animateManifestReorder(
    listEl: HTMLElement,
    scrollToItemId: number | null,
    durationMs: number = PANTRY_REORDER_MS,
): void {
    const sorted = sortItems(state.items, state.sortBy, state.searchQuery);
    const rows = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")];
    if (rows.length === 0) return;

    const topsBefore = new Map<number, number>();
    for (const row of rows) {
        const id = Number(row.dataset.itemId);
        if (id) topsBefore.set(id, row.getBoundingClientRect().top);
    }

    for (const item of sorted) {
        const row = getSwipeRowForItem(listEl, item.id);
        if (row) listEl.appendChild(row);
    }

    void listEl.offsetHeight;

    const scrollTarget = scrollToItemId ? getSwipeRowForItem(listEl, scrollToItemId) : null;
    const movers: { row: HTMLElement; delta: number; active: boolean }[] = [];

    for (const item of sorted) {
        const row = getSwipeRowForItem(listEl, item.id);
        if (!row) continue;

        const beforeTop = topsBefore.get(item.id);
        if (beforeTop === undefined) continue;

        const delta = beforeTop - row.getBoundingClientRect().top;
        if (Math.abs(delta) < 1) continue;

        movers.push({
            row,
            delta,
            active: scrollToItemId !== null && item.id === scrollToItemId,
        });
    }

    const animations: Animation[] = [];

    for (const { row, delta, active } of movers) {
        resetPantryRowMotion(row);
        row.classList.add("swipe-row--reordering");
        row.classList.add(active ? "swipe-row--reorder-active" : "swipe-row--reorder-partner");
        row.style.willChange = "transform";
        const anim = row.animate(
            [{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }],
            { duration: durationMs, easing: PANTRY_EASE, fill: "forwards" },
        );
        animations.push(anim);
    }

    window.setTimeout(() => {
        for (const { row } of movers) {
            row.classList.remove(
                "swipe-row--reordering",
                "swipe-row--reorder-active",
                "swipe-row--reorder-partner",
            );
            row.style.removeProperty("will-change");
        }
        for (const anim of animations) {
            anim.cancel();
        }
        if (scrollTarget) {
            scrollRowIntoViewClearingNav(scrollTarget);
        }
    }, durationMs + 40);
}

const flushQuantityReorder = debounce(() => {
    if (!isQuantitySort()) return;

    const listEl = document.getElementById("pantry-manifest");
    if (!listEl) return;

    animateManifestReorder(listEl, lastQuantityChangeId);
    lastQuantityChangeId = null;
}, QUANTITY_REORDER_DEBOUNCE_MS);

function scheduleQuantityReorder(itemId: number): void {
    if (!isQuantitySort()) return;
    lastQuantityChangeId = itemId;
    flushQuantityReorder();
}

async function setQuantityAbsolute(
    itemId: number,
    newQty: number,
    manifestRow: HTMLElement,
): Promise<void> {
    const item = state.items.find((i) => i.id === itemId);
    if (!item) return;

    const countEl = manifestRow.querySelector<HTMLElement>(".tally-count");
    const prevQty = Number(countEl?.dataset.value ?? item.quantity);
    const clamped = Math.max(0, Math.min(9999, Math.floor(newQty)));
    if (clamped === prevQty) return;

    item.quantity = clamped;
    setTallyQuantity(manifestRow, item.name, clamped);

    try {
        await api.items.update(itemId, { quantity: clamped });
        scheduleQuantityReorder(itemId);
    } catch {
        item.quantity = prevQty;
        setTallyQuantity(manifestRow, item.name, prevQty);
    }
}

async function changeQuantity(
    itemId: number,
    delta: number,
    manifestRow: HTMLElement,
): Promise<void> {
    const item = state.items.find((i) => i.id === itemId);
    if (!item) return;

    const countEl = manifestRow.querySelector<HTMLElement>(".tally-count");
    const prevQty = Number(countEl?.dataset.value ?? item.quantity);

    if (delta < 0 && prevQty <= 0) {
        beginDeferredItemRemoval(item);
        return;
    }

    const newQty = Math.max(0, prevQty + delta);
    const direction = delta > 0 ? "up" : "down";

    item.quantity = newQty;
    applyManifestRowQuantity(manifestRow, item.name, newQty, direction);

    try {
        if (delta < 0) {
            await api.items.decrement(itemId);
        } else {
            await api.items.update(itemId, { quantity: newQty });
        }
        scheduleQuantityReorder(itemId);
    } catch {
        item.quantity = prevQty;
        applyManifestRowQuantity(
            manifestRow,
            item.name,
            prevQty,
            direction === "up" ? "down" : "up",
        );
    }
}

function applyCascadeGlide(el: HTMLElement, stagger: number): void {
    el.classList.add("cascade-glide");
    el.style.setProperty("--stagger", String(stagger));
}

function triggerViewEnter(page: HTMLElement): void {
    page.classList.remove("view-enter");
    void page.offsetWidth;
    page.classList.add("view-enter");
}

function renderPantryLoadingShell(container: HTMLElement): void {
    container.innerHTML = "";

    const page = document.createElement("div");
    page.className = "pantry-page pantry-page--loading view-enter";

    const header = document.createElement("div");
    header.className = "pantry-header";

    const searchLedger = document.createElement("div");
    searchLedger.className = "search-ledger";
    applyCascadeGlide(searchLedger, 0);

    const search = document.createElement("div");
    search.className = "pantry-shimmer-search loading-shimmer";
    const addShimmer = document.createElement("span");
    addShimmer.className = "pantry-shimmer-add loading-shimmer";
    searchLedger.appendChild(search);
    searchLedger.appendChild(addShimmer);

    const chips = document.createElement("div");
    chips.className = "pantry-shimmer-chips";
    for (let i = 0; i < 4; i++) {
        const chip = document.createElement("span");
        chip.className = "pantry-shimmer-chip loading-shimmer cascade-glide";
        chip.style.setProperty("--stagger", String(1 + i));
        chips.appendChild(chip);
    }

    const rows = document.createElement("div");
    rows.className = "pantry-shimmer-rows";
    for (let i = 0; i < 7; i++) {
        const row = document.createElement("div");
        row.className = "pantry-shimmer-row loading-shimmer cascade-glide";
        row.style.setProperty("--stagger", String(2 + Math.min(i, 5)));
        rows.appendChild(row);
    }

    header.appendChild(searchLedger);
    header.appendChild(chips);
    header.appendChild(rows);
    page.appendChild(header);
    container.appendChild(page);
}

function waitAnimation(el: HTMLElement, fallbackMs: number): Promise<void> {
    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            el.removeEventListener("animationend", onEnd);
            resolve();
        };
        const onEnd = (e: AnimationEvent) => {
            if (e.target === el) finish();
        };
        el.addEventListener("animationend", onEnd);
        window.setTimeout(finish, fallbackMs);
    });
}

async function transitionToPantryContent(container: HTMLElement, gen: number): Promise<void> {
    const loadingPage = container.querySelector<HTMLElement>(".pantry-page--loading");
    if (loadingPage) {
        loadingPage.classList.add("pantry-page--exiting");
        await waitAnimation(loadingPage, 340);
    }
    if (isNavigationStale(gen)) return;
    renderPantryContent(container);
}

export async function renderPantry(container: HTMLElement, gen: number): Promise<void> {
    if (isNavigationStale(gen)) return;
    applyPantryFiltersFromHash();
    renderPantryLoadingShell(container);

    const refreshOnSync = async () => {
        if (isNavigationStale(gen)) return;
        const [locations, categories] = await Promise.all([
            api.locations.list(),
            api.items.categories(),
        ]);
        if (isNavigationStale(gen)) return;
        state.locations = locations;
        state.categories = categories;
        await loadItems();
        if (isNavigationStale(gen)) return;
        const listEl = container.querySelector<HTMLElement>("#pantry-manifest");
        if (listEl) {
            await syncPantryManifestFromState(listEl, container);
            updateCategoryChips(container);
        }
    };
    window.addEventListener("pantry-sync", refreshOnSync);

    const onIdRemap = (e: Event) => {
        const { table, tempId, serverId } = (e as CustomEvent).detail;
        if (table !== "items") return;
        const listEl = container.querySelector<HTMLElement>("#pantry-manifest");
        if (!listEl) return;
        const row = listEl.querySelector<HTMLElement>(`.swipe-row[data-item-id="${tempId}"]`);
        if (row) {
            row.dataset.itemId = String(serverId);
        }
        const item = state.items.find((i) => i.id === tempId);
        if (item) {
            item.id = serverId;
        }
    };
    window.addEventListener("pantry-id-remap", onIdRemap);

    try {
        const [locations, categories] = await Promise.all([
            api.locations.list(),
            api.items.categories(),
        ]);
        if (isNavigationStale(gen)) return;
        state.locations = locations;
        state.categories = categories;
        await loadItems();
        if (isNavigationStale(gen)) return;
        await transitionToPantryContent(container, gen);
    } catch {
        if (isNavigationStale(gen)) return;
        container.innerHTML = `
      <div class="empty-state pantry-empty cascade-glide">
        <p>${msg("pantry/load-error")}</p>
      </div>
    `;
    }
}

async function loadItems(): Promise<void> {
    const filters: Parameters<typeof api.items.list>[0] = {};
    if (state.locationFilter) filters.location = state.locationFilter;
    if (state.categoryFilter) filters.category = state.categoryFilter;
    if (state.expiringFilter) {
        if (state.expiringFilter === "expired") {
            filters.expired = true;
        } else {
            filters.expiringWithin = Number(state.expiringFilter);
        }
    }
    if (state.stockedFilter !== "") filters.stocked = state.stockedFilter === "1";
    const items = await api.items.list(filters);
    state.items = items.filter((i) => !pendingItemRemovals.has(i.id));
}

function openAddSheet(container: HTMLElement, draft?: ScanAddDraft): void {
    showItemSheet({
        mode: "add",
        locations: state.locations,
        categories: state.categories,
        draft: draft ?? null,
        onSaved: async (created) => {
            await promptGroceryLinksForItem(created);
            await loadItems();
            const listEl = container.querySelector<HTMLElement>("#pantry-manifest");
            if (listEl) await syncPantryManifestFromState(listEl, container);
        },
        onExistingBarcode: (item) => openEditSheet(container, item),
    });
}

function openEditSheet(container: HTMLElement, item: Item): void {
    showItemSheet({
        mode: "edit",
        item,
        locations: state.locations,
        categories: state.categories,
        onSaved: async () => {
            await loadItems();
            const listEl = container.querySelector<HTMLElement>("#pantry-manifest");
            if (listEl) await syncPantryManifestFromState(listEl, container);
        },
        onDeleted: (deleteItem) => beginDeferredItemRemoval(deleteItem),
    });
}

async function handleBarcodeSearch(container: HTMLElement, upc: string): Promise<void> {
    try {
        const items = await api.barcode.findItems(upc);
        if (items.length === 1) {
            openEditSheet(container, items[0]);
        } else if (items.length > 1) {
            showPicker(
                "Multiple items found",
                items,
                (item) => formatItemPickerLabel(item, items),
                (item) => openEditSheet(container, item),
            );
        } else {
            const content = document.createElement("div");
            content.className = "barcode-prompt";

            const message = document.createElement("p");
            message.textContent = `No item found for barcode ${upc}.`;
            content.appendChild(message);

            const actions = document.createElement("div");
            actions.className = "barcode-prompt-actions";

            const addBtn = document.createElement("button");
            addBtn.type = "button";
            addBtn.className = "btn btn-primary btn-block";
            addBtn.textContent = "Add this item";
            addBtn.addEventListener("click", () => {
                close();
                openAddSheet(container, {
                    barcode: upc,
                    name: "",
                    brand: null,
                    category: null,
                    image_path: null,
                });
            });
            actions.appendChild(addBtn);

            const cancelBtn = document.createElement("button");
            cancelBtn.type = "button";
            cancelBtn.className = "btn btn-secondary btn-block";
            cancelBtn.textContent = "Cancel";
            cancelBtn.addEventListener("click", () => close());
            actions.appendChild(cancelBtn);

            content.appendChild(actions);

            let close: () => void;
            close = showSheet("No item found", content, undefined, { dismissible: true });
        }
    } catch {
        /* silent */
    }
}

function openFilterSheet(container: HTMLElement): void {
    const content = document.createElement("div");
    content.className = "pantry-filter-sheet";

    const locationField = document.createElement("div");
    locationField.className = "ledger-field";
    locationField.innerHTML = `<label class="ledger-label" for="filter-location">Location</label>`;
    const locationSelect = document.createElement("select");
    locationSelect.id = "filter-location";
    locationSelect.className = "ledger-input";
    locationSelect.innerHTML = '<option value="">All locations</option>';
    for (const loc of state.locations) {
        const opt = document.createElement("option");
        opt.value = String(loc.id);
        opt.textContent = loc.name;
        opt.selected = state.locationFilter === loc.id;
        locationSelect.appendChild(opt);
    }
    locationField.appendChild(locationSelect);

    const stockField = document.createElement("div");
    stockField.className = "ledger-field";
    stockField.innerHTML = `<label class="ledger-label" for="filter-stocked">Stock</label>`;
    const stockSelect = document.createElement("select");
    stockSelect.id = "filter-stocked";
    stockSelect.className = "ledger-input";
    stockSelect.innerHTML = `
    <option value="">Any stock</option>
    <option value="1">In stock</option>
    <option value="0">Out of stock</option>
  `;
    stockSelect.value = state.stockedFilter;
    stockField.appendChild(stockSelect);

    const expiryField = document.createElement("div");
    expiryField.className = "ledger-field";
    expiryField.innerHTML = `<label class="ledger-label" for="filter-expiry">Expiry</label>`;
    const expirySelect = document.createElement("select");
    expirySelect.id = "filter-expiry";
    expirySelect.className = "ledger-input";
    expirySelect.innerHTML = `
    <option value="">Any expiry</option>
    <option value="expired">Expired</option>
    <option value="2">Expiring ≤2 days</option>
    <option value="7">Expiring ≤7 days</option>
    <option value="14">Expiring ≤14 days</option>
  `;
    expirySelect.value = state.expiringFilter;
    expiryField.appendChild(expirySelect);

    const applyBtn = document.createElement("button");
    applyBtn.type = "button";
    applyBtn.className = "pantry-filter-apply";
    applyBtn.textContent = "Apply filters";

    const clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "pantry-filter-clear";
    clearBtn.textContent = "Clear filters";
    clearBtn.disabled = !hasActiveFilters();

    applyCascadeGlide(locationField, 0);
    applyCascadeGlide(stockField, 1);
    applyCascadeGlide(expiryField, 2);
    applyCascadeGlide(applyBtn, 3);
    applyCascadeGlide(clearBtn, 4);

    content.appendChild(locationField);
    content.appendChild(stockField);
    content.appendChild(expiryField);
    content.appendChild(applyBtn);
    content.appendChild(clearBtn);

    const close = showSheet("Filter", content, undefined, { dismissible: true });

    applyBtn.addEventListener("click", async () => {
        const selected = locationSelect.value;
        state.locationFilter = selected ? Number(selected) : null;
        state.stockedFilter = stockSelect.value;
        state.expiringFilter = expirySelect.value;
        close();
        await refreshList(container);
        updateToolbarBtnState(container);
    });

    clearBtn.addEventListener("click", async () => {
        state.locationFilter = null;
        state.stockedFilter = "";
        state.expiringFilter = "";
        close();
        await refreshList(container);
        updateToolbarBtnState(container);
    });
}

function openSortSheet(container: HTMLElement): void {
    const content = document.createElement("div");
    content.className = "pantry-sort-sheet";

    const list = document.createElement("div");
    list.className = "pantry-sort-list";

    const sortEntries = Object.entries(ITEM_SORT_LABELS) as [ItemSortOption, string][];
    for (let i = 0; i < sortEntries.length; i++) {
        const [value, label] = sortEntries[i];
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `pantry-sort-option cascade-glide${state.sortBy === value ? " active" : ""}`;
        btn.style.setProperty("--stagger", String(i));
        btn.textContent = label;
        btn.addEventListener("click", async () => {
            state.sortBy = value;
            close();
            await refreshList(container, { reload: false });
            updateToolbarBtnState(container);
        });
        list.appendChild(btn);
    }

    content.appendChild(list);
    const close = showSheet("Sort", content, undefined, { dismissible: true });
}

function updateToolbarBtnState(container: HTMLElement): void {
    const filterBtn = container.querySelector(".pantry-tool-btn--filter");
    filterBtn?.classList.toggle("is-active", hasActiveFilters());
}

function renderPantryContent(container: HTMLElement): void {
    container.innerHTML = "";

    const page = document.createElement("div");
    page.className = "pantry-page";

    const header = document.createElement("div");
    header.className = "pantry-header";

    const searchLedger = document.createElement("div");
    searchLedger.className = "search-ledger";
    applyCascadeGlide(searchLedger, 0);

    const search = document.createElement("input");
    search.type = "search";
    search.className = "search-input";
    search.placeholder = "Search pantry...";
    search.value = state.searchQuery;
    search.addEventListener(
        "input",
        debounce(() => {
            state.searchQuery = search.value.trim();
            void refreshList(container, { reload: false });
        }, 250),
    );

    const scanBtn = document.createElement("button");
    scanBtn.type = "button";
    scanBtn.className = "search-scan-btn";
    scanBtn.setAttribute("aria-label", "Scan barcode");
    scanBtn.innerHTML = `<span class="material-symbols-outlined">photo_camera</span>`;
    scanBtn.addEventListener("click", () => {
        void openScannerOverlay().then((upc) => {
            if (upc) void handleBarcodeSearch(container, upc);
        });
    });

    searchLedger.appendChild(search);
    searchLedger.appendChild(scanBtn);

    const chipRow = document.createElement("div");
    chipRow.className = "filter-chips filter-chips--scroll";

    let chipStagger = 0;
    const allChip = makeCategoryChip("All", state.categoryFilter === "", () => {
        state.categoryFilter = "";
        void refreshList(container);
    });
    applyCascadeGlide(allChip, 1 + chipStagger++);
    chipRow.appendChild(allChip);

    for (const cat of state.categories) {
        const chip = makeCategoryChip(
            cat,
            state.categoryFilter === cat,
            () => {
                state.categoryFilter = cat;
                void refreshList(container);
            },
            cat,
        );
        applyCascadeGlide(chip, 1 + Math.min(chipStagger++, 8));
        chipRow.appendChild(chip);
    }

    const toolbar = document.createElement("div");
    toolbar.className = "pantry-toolbar";
    applyCascadeGlide(toolbar, 2);

    const filterBtn = document.createElement("button");
    filterBtn.type = "button";
    filterBtn.className = `pantry-tool-btn pantry-tool-btn--filter${hasActiveFilters() ? " is-active" : ""}`;
    filterBtn.setAttribute("aria-label", "Filter by location and expiry");
    filterBtn.appendChild(createIcon("filter_list", "icon-sm"));
    filterBtn.addEventListener("click", () => openFilterSheet(container));

    const sortBtn = document.createElement("button");
    sortBtn.type = "button";
    sortBtn.className = "pantry-tool-btn pantry-tool-btn--sort";
    sortBtn.setAttribute("aria-label", "Sort items");
    sortBtn.appendChild(createIcon("sort", "icon-sm"));
    sortBtn.addEventListener("click", () => openSortSheet(container));

    toolbar.appendChild(filterBtn);
    toolbar.appendChild(sortBtn);

    header.appendChild(searchLedger);
    header.appendChild(chipRow);
    header.appendChild(toolbar);

    const listEl = document.createElement("div");
    listEl.className = "pantry-manifest";
    listEl.id = "pantry-manifest";

    const fab = document.createElement("button");
    fab.type = "button";
    fab.className = "pantry-fab pantry-fab--enter";
    fab.setAttribute("aria-label", "Add item");
    fab.innerHTML = `<span class="material-symbols-outlined">add</span>`;
    fab.addEventListener("click", () => openAddSheet(container));

    page.appendChild(header);
    page.appendChild(listEl);
    container.appendChild(page);
    container.appendChild(fab);

    triggerViewEnter(page);
    renderItemList(listEl, container, "initial");
    focusPantryItem(listEl, pendingFocusItemId);
    pendingFocusItemId = null;
}

function makeCategoryChip(
    label: string,
    active: boolean,
    onClick: () => void,
    category?: string,
): HTMLButtonElement {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = `chip${active ? " active" : ""}`;
    if (category !== undefined) chip.dataset.category = category;
    chip.textContent = label;
    chip.addEventListener("click", onClick);
    return chip;
}

function updateCategoryChips(container: HTMLElement): void {
    const chipRow = container.querySelector<HTMLElement>(".filter-chips");
    if (!chipRow) return;

    const existingCategories = new Set<string>();
    chipRow.querySelectorAll<HTMLButtonElement>(".chip").forEach((chip) => {
        const cat = chip.dataset.category;
        if (cat !== undefined) existingCategories.add(cat);
    });

    const currentCategories = new Set(state.categories);
    const needsRebuild =
        existingCategories.size !== currentCategories.size ||
        [...existingCategories].some((cat) => !currentCategories.has(cat)) ||
        [...currentCategories].some((cat) => !existingCategories.has(cat));

    if (!needsRebuild) {
        const chips = chipRow.querySelectorAll<HTMLButtonElement>(".chip");
        chips.forEach((chip, index) => {
            if (index === 0) {
                chip.classList.toggle("active", state.categoryFilter === "");
                return;
            }
            const cat = chip.dataset.category;
            chip.classList.toggle("active", cat ? state.categoryFilter === cat : false);
        });
        return;
    }

    chipRow.replaceChildren();

    const allChip = makeCategoryChip("All", state.categoryFilter === "", () => {
        state.categoryFilter = "";
        void refreshList(container);
    });
    chipRow.appendChild(allChip);

    for (const cat of state.categories) {
        const chip = makeCategoryChip(
            cat,
            state.categoryFilter === cat,
            () => {
                state.categoryFilter = cat;
                void refreshList(container);
            },
            cat,
        );
        chipRow.appendChild(chip);
    }
}

async function refreshList(
    container: HTMLElement,
    options: { reload?: boolean } = { reload: true },
): Promise<void> {
    const chipRow = container.querySelector(".filter-chips");
    if (chipRow) {
        const chips = chipRow.querySelectorAll<HTMLButtonElement>(".chip");
        chips.forEach((chip, index) => {
            if (index === 0) {
                chip.classList.toggle("active", state.categoryFilter === "");
                return;
            }
            const cat = chip.dataset.category;
            chip.classList.toggle("active", cat ? state.categoryFilter === cat : false);
        });
    }

    const listEl = container.querySelector<HTMLElement>("#pantry-manifest");
    if (options.reload !== false) {
        await loadItems();
        if (listEl) await syncPantryManifestFromState(listEl, container);
    } else if (listEl) {
        syncPantryManifestOrder(listEl, null, PANTRY_SEARCH_REORDER_MS);
    }
    updateToolbarBtnState(container);
}

function renderItemList(
    listEl: HTMLElement,
    container: HTMLElement,
    entrance: ListEntrance = "refresh",
): void {
    manifestGeneration++;
    listEl.innerHTML = "";
    listEl.classList.remove("pantry-manifest--refresh");

    const items = sortItems(state.items, state.sortBy, state.searchQuery);

    if (items.length === 0) {
        const empty = createPantryEmptyElement(container);
        empty.className = `empty-state pantry-empty${entrance === "initial" ? " cascade-glide" : " manifest-enter"}`;
        if (entrance === "initial") {
            empty.style.setProperty("--stagger", String(MANIFEST_STAGGER_BASE));
        }
        listEl.appendChild(empty);
        return;
    }

    if (entrance === "refresh") {
        listEl.classList.add("pantry-manifest--refresh");
    }

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const swipeRow = buildSwipeRowForItem(item, container);

        if (entrance === "initial") {
            swipeRow.classList.add("cascade-glide");
            swipeRow.style.setProperty(
                "--stagger",
                String(MANIFEST_STAGGER_BASE + Math.min(i, MANIFEST_STAGGER_CAP)),
            );
        } else {
            swipeRow.style.setProperty("--stagger", String(Math.min(i, 10)));
        }

        listEl.appendChild(swipeRow);
    }
}

export function beginDeferredItemRemoval(item: Item): void {
    void beginDeferredItemRemovalAsync(item);
}

async function beginDeferredItemRemovalAsync(item: Item): Promise<void> {
    const listEl = document.getElementById("pantry-manifest");
    const rowEl = listEl ? getSwipeRowForItem(listEl, item.id) : null;
    if (!listEl || !rowEl) return;

    pendingItemRemovals.add(item.id);
    const anchor = getPantryRemovalAnchor(rowEl);
    state.items = state.items.filter((i) => i.id !== item.id);

    const removed = await removePantryRowAnimated(listEl, item.id);
    if (!removed) {
        pendingItemRemovals.delete(item.id);
        return;
    }

    showUndoToast({
        message: msg("toast/removed", { name: item.name }),
        durationMs: 5000,
        onUndo: () => {
            void restoreItemOnPantry(item, anchor);
        },
        onComplete: async () => {
            try {
                await api.items.delete(item.id);
            } catch {
                void restoreItemOnPantry(item, anchor);
            } finally {
                pendingItemRemovals.delete(item.id);
            }
        },
    });
}

export function finalizePendingItemRemoval(): void {
    finalizePendingToast();
}

export async function restoreItemOnPantry(item: Item, anchor?: PantryRemovalAnchor): Promise<void> {
    pendingItemRemovals.delete(item.id);
    if (state.items.find((i) => i.id === item.id)) return;

    const listEl = document.getElementById("pantry-manifest");
    const container = document.getElementById("app-main");
    if (!listEl || !container) return;

    state.items.push(item);

    await removePantryEmptyAnimated(listEl);

    const rowEl = buildSwipeRowForItem(item, container);
    const beforeEl =
        anchor?.beforeId != null
            ? getSwipeRowForItem(listEl, anchor.beforeId)
            : findPantryInsertBefore(listEl, item);

    await insertPantryRowAnimated(listEl, rowEl, beforeEl);
}
